import { createSelector } from 'reselect';
import { APIRemoteUrl, API_ASE_IMAGE } from 'service/api/constants';
import { selectUrlToken } from 'store/auth/selectors';
import { State } from 'store/types';
import {
  FilesContextEnum,
  GENERIC_IMAGE_GRID_FIT_FILE_MANAGER,
  GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER,
  ImageGridFitEnums,
  ImageGridSizeEnums,
  ImageGridSizes,
  createSelectorCached,
} from 'types/types';

import { selectGeneric } from 'store/app/selectors';
import {
  FileManagerShowImageFilenames,
  FileManagerViewType,
  GenericKeyFileManagerShowImageFilenames,
  GenericKeyFileManagerViewType,
} from 'screens/FileManager/types';
import { validateEnumFallback } from 'lib/riskbase';
import { FilesState } from './types';

const selectState = (state: State): FilesState => state.files;

export const selectFilesContext = createSelector(
  selectState,
  (state) => state.context,
);

export const selectFilesImageRemoteUrl = createSelectorCached(
  [
    selectFilesContext,
    (_: State, size?: Parameters<typeof API_ASE_IMAGE>[1]['size']) => size,
    selectUrlToken,
  ],
  (context, size, urlToken): APIRemoteUrl | undefined =>
    context?.context === FilesContextEnum.remoteAssessment
      ? API_ASE_IMAGE(context.assessmentId, { urlToken, size })
      : undefined,
);

export const selectFilesFiles = createSelector(
  selectState,
  (state) => state.files,
);

export const selectFilesImageGridSize = createSelector(
  (state: State) => selectGeneric(state, GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER),
  (
    genericGridSize: unknown,
  ): (typeof ImageGridSizes)[keyof typeof ImageGridSizes] =>
    genericGridSize && ImageGridSizes[genericGridSize as ImageGridSizeEnums]
      ? ImageGridSizes[genericGridSize as ImageGridSizeEnums]
      : ImageGridSizes[ImageGridSizeEnums.s],
);

export const selectFileImageGridFit = createSelector(
  (state: State) => selectGeneric(state, GENERIC_IMAGE_GRID_FIT_FILE_MANAGER),
  (genericGridFit: unknown): ImageGridFitEnums =>
    genericGridFit === ImageGridFitEnums.aspect
      ? ImageGridFitEnums.aspect
      : ImageGridFitEnums.square,
);

export const selectFilesViewType = createSelector(
  (state: State) => selectGeneric(state, GenericKeyFileManagerViewType),
  (genericViewType: unknown): FileManagerViewType =>
    validateEnumFallback(
      FileManagerViewType,
      genericViewType,
      FileManagerViewType.grid,
    ),
);

export const selectFilesShowImageFilename = createSelector(
  (state: State) =>
    selectGeneric(state, GenericKeyFileManagerShowImageFilenames),
  (showImageFilenames: unknown): FileManagerShowImageFilenames =>
    validateEnumFallback(
      FileManagerShowImageFilenames,
      showImageFilenames,
      FileManagerShowImageFilenames.hide,
    ),
);
